import axios from 'axios';
import { reachGoal } from '../utils/yandexMetrica';

export const CREATE_COURSE = `/coursework/createCourse`;
export const CREATE_FOLDER = `/coursework/createFolder`;
export const GET_COURSES_FOLDERS = `/coursework/getCoursesAndFolders`;
export const GET_STUDENT_COURSES = `/coursework/getCoursesForStudents`;
export const GET_COURSES = `/coursework/getCourses`;
export const GET_FOLDERS = `/coursework/getFolders`;
export const MOVE_COURSE = `/coursework/moveCourse`;
export const GET_UNITS = `/coursework/getUnits`;
export const GET_STUDENT_UNITS = `/coursework/getStudentUnits`;
export const CREATE_UNIT = `/coursework/createUnit`
export const GET_LESSONS = `/lessonwork/getLessons`;
export const CREATE_LESSON = `/lessonwork/createLesson`;
export const GET_FOLDER_STRUCTURE = `/coursework/getFolderStructure`;
export const GET_UNIT_STRUCTURE = `/coursework/getUnitStructure`;
export const DELETE_ITEM = `/coursework/deleteItem`;
export const RENAME_ITEM = `/coursework/handleRename`;
export const MOVE_ITEM = `/coursework/moveItem`;
export const GET_TASKS = `/lessonwork/getTasks`;
export const GET_TEST_TASKS = `/lessonwork/getTestTasks`;
export const MOVE_UP = `/coursework/moveUp`;
export const GET_PARENT_FOLDER = `/coursework/getParentFolder`;
export const CREATE_TASK = `/lessonwork/createTask`;

const isProduction = process.env.REACT_APP_MODE === 'prod';
const base = isProduction ? `/api` : `${process.env.REACT_APP_API_URL}/api`;

const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
  withCredentials: true,
});

let refreshPromise: Promise<any> | null = null;
let lastRefreshTime = 0;

// Stashed by setupAxiosInterceptors so the fetch()-based upload helpers below
// (which can't go through axios's interceptor) can share the exact same
// refresh-and-retry flow instead of just failing on an expired token.
let refreshTokenFn: (() => Promise<void>) | null = null;
let logoutFn: (() => void) | null = null;
let onSessionExpiredFn: (() => void) | null = null;

export function setupAxiosInterceptors(
  refreshToken: () => Promise<void>,
  logout: () => void,
  onSessionExpired: () => void
) {
  refreshTokenFn = refreshToken;
  logoutFn = logout;
  onSessionExpiredFn = onSessionExpired;

  api.interceptors.response.use(
    response => response,
    async error => {
      const status = error.response?.status;

      if (error.response && status === 403) {
        logout();
        onSessionExpired();
        return Promise.reject(error);
      }

      if (error.response && status === 401) {
        const refreshed = await tryRefreshAccessToken();
        if (!refreshed) {
          return Promise.reject(error);
        }
        // retry original request with new token
        return api.request(error.config);
      }

      return Promise.reject(error);
    }
  );
}

// Shared by the axios interceptor above and fetchWithAuthRetry below. Returns
// true once a fresh access token is in place (safe to retry the request),
// false if the caller should give up (session expired, or refresh on cooldown).
async function tryRefreshAccessToken(): Promise<boolean> {
  if (!refreshTokenFn || !logoutFn || !onSessionExpiredFn) return false;

  try {
    const now = Date.now();

    // If we already have a refresh in progress, wait for it
    if (refreshPromise) {
      await refreshPromise;
    }
    // Otherwise, start a new refresh (with cooldown)
    else if (now - lastRefreshTime > 30_000) {
      lastRefreshTime = now;
      refreshPromise = refreshTokenFn()
        .catch(err => {
          if (axios.isAxiosError(err)) {
            logoutFn?.();
            onSessionExpiredFn?.();
          }
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
      await refreshPromise;
    } else {
      console.warn("Skipping refresh: cooldown active");
      logoutFn();
      onSessionExpiredFn();
      return false;
    }

    return true;
  } catch {
    logoutFn();
    onSessionExpiredFn();
    return false;
  }
}

// fetch() has no interceptor to hook into, so the upload helpers below (which
// use fetch instead of the `api` axios instance — see git history on this
// file for why) call this instead of fetch() directly, so an expired access
// token gets silently refreshed and the upload retried, the same way any
// other request already does.
async function fetchWithAuthRetry(input: string, init: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401) {
    return response;
  }

  const refreshed = await tryRefreshAccessToken();
  if (!refreshed) {
    return response;
  }

  return fetch(input, init);
}

export const fetchCoursesAndFolders = async () => {
  const response = await api.get(`/coursework/getCoursesAndFolders`);
  return response.data;
};

export const fetchCourses = async () => {
  const response = await api.get(`/coursework/getCourses`);
  return response.data;
}

export const fetchCoursesForStudents = async () => {
  const response = await api.get(`/coursework/getCoursesForStudents`);
  return response.data.courses;
};

export const createCourse = async (courseTitle: string, courseImage?: string) => {
  const response = await api.post(`/coursework/createCourse`, { courseTitle, courseImage });
  reachGoal('COURSE_CREATED');
  return response.data;
};

export const createFolder = async (folderName: string, parentId?: number) => {
  const response = await api.post(`/coursework/createFolder`, { folderName, parentId });
  return response.data;
};

export const fetchFolders = async (folderId: number) => {
  const response = await api.get(`/coursework/getFolders/${folderId}`);
  return response.data;
};

export const moveCourse = async (folderId: number | null, courseId: number) => {
  await api.post(`/coursework/moveCourse`, { folderId, courseId });
};

export const fetchUnits = async (courseId: Number) => {
  const response = await api.get(`/coursework/getUnits/${courseId}`);
  return response.data;
};

export const fetchStudentUnits = async (courseId: string) => {
  const response = await api.get(`/coursework/getStudentUnits/${courseId}`);
  return response.data;
}

export const createUnit = async (courseId: number, unitTitle: string, unitImage: string | null, unitOrder: number) => {
  const response = await api.post(`/coursework/createUnit`, { courseId, unitTitle, unitImage, unitOrder });
  return response.data;
};

export const fetchLessons = async (unitId: number) => {
  const response = await api.get(`/lessonwork/getLessons`, { params: { unitId } });
  return response.data;
};

export const fetchLesson = async (lessonId: string) => {
  const response = await api.get(`/lessonwork/getLesson`, { params: { lessonId } });
  return response.data;
}

export const createLesson = async (unitId: number, lessonTitle: string, isTest: number, lessonImage: string | null, lessonOrder: number) => {
  const response = await api.post(`/lessonwork/createLesson`, { unitId, lessonTitle, isTest, lessonImage, lessonOrder });
  reachGoal('LESSON_CREATED');
  return response.data;
};

export const fetchFolderStructure = async () => {
  const response = await api.get(`/coursework/getFolderStructure`);
  return response.data;
};

export const fetchUnitStructure = async () => {
  const response = await api.get(`/coursework/getUnitStructure`);
  return response.data;
};

export const deleteItem = async (itemType: string, itemId: number) => {
  await api.delete(`/coursework/deleteItem`, { data: { itemType, itemId } });
};

export const copyItem = async (itemType: string, itemId: number) => {
  const { data } = await api.post(`/coursework/copyItem`, { itemType, itemId });
  return data.newItem; // return the copied object (Unit, Course, or Lesson)
};

export const deleteCourseImage = async (imgPath: string) => {
  await api.delete(`/filework/deleteCourseImage`, { data: { imgPath } });
};

export const editItem = async (itemType: string, itemId: number, newName: string, newImage: string | null) => {
  await api.put(`/coursework/handleEdit`, { itemType, itemId, newName, newImage });
};

export const moveItem = async (itemType: string, itemId: number, targetId: number | null) => {
  await api.post(`/coursework/moveItem`, { itemType, itemId, targetId });
};

export const fetchTasks = async (lessonId: number) => {
  const response = await api.get(`/lessonwork/getTasks/${lessonId}`);
  return response.data;
};

export const fetchTestTasks = async (lessonId: number, studentId: number, attempt: number) => {
  const response = await api.get(`/lessonwork/getTestTasks`, { params: { lessonId, studentId, attempt } });
  return response.data;
};

export const moveUp = async (folderId: number, courseId: number) => {
  const response = await api.post(`/coursework/moveUp`, { folderId, courseId });
  return response.data;
};

export const getParentFolder = async (folderId: number) => {
  const response = await api.get(`/coursework/getParentFolder/${folderId}`);
  return response.data;
};


export const fetchAttendance = async (studentId: number | undefined, courseId: number | undefined, limit: number, offset: number) => {
  const response = await api.get(`/coursework/getAttendance`, {
    params: {
      studentId,
      courseId,
      limit,
      offset
    },
  });
  return response.data;
}

export const saveAllAttendance = async (studentId: number | undefined, courseId: number | undefined, attendance: any) => {
  const response = await api.put('/coursework/saveAllAttendance', { studentId, courseId, attendance });
  return response.data;
}

export const saveAttendance = async (courseId: number | undefined, attendance: any, date: any) => {
  const response = await api.put('/coursework/saveAttendance', { courseId, attendance, date });
  return response.data;
}

export const fetchGroupContent = async (groupId: number) => {
  const response = await api.get(`/coursework/getGroupContent/${groupId}`);
  return response.data;
}

export const openCourse = async (studentId: number, courseId: number) => {
  const response = await api.put(`/coursework/openCourse`, { studentId, courseId });
  return response.data;
}

export const moveStudent = async (studentId: number, courseId: number, groupId: number, includePastAttendance = false) => {
  const response = await api.put(`/coursework/moveStudent`, { studentId, courseId, groupId, includePastAttendance });
  return response.data;
}

export const removeStudentFromGroup = async (studentId: number, courseId: number) => {
  const response = await api.put(`/coursework/removeStudentFromGroup`, { studentId, courseId});
  return response.data;
}

export const fetchCourseLessonLogs = async (studentId: number | null, courseId: number, studentGroup: number | null, limit: number, offset: number) => {
  const response = await api.get(`/coursework/getCourseLessonLogs`, {
    params: {
      studentId,
      courseId,
      studentGroup,
      limit,
      offset,
    },
  });
  return response.data;
}

export const addLessonLog = async (classId: number, courseId: number, date: string, materialCovered: string, logNotes: string) => {
  const response = await api.post('/coursework/addLessonLog', { classId, courseId, date, materialCovered, logNotes });
  return response;
}

export const addKnowledgeContent = async (knowledgesId: number, classId: number, courseId: number, unitId: number, lessonId: number, taskId: number|null, title: string) => {
  const response = await api.post('/coursework/addKnowledgeContent', { knowledgesId, classId, courseId, unitId, lessonId, taskId, title });
  return response;
}

export const editLessonLog = async (logId: number, classId: number, courseId: number, date: string, materialCovered: string, logNotes: string) => {
  const response = await api.post('/coursework/editLessonLog', { logId, classId, courseId, date, materialCovered, logNotes });
  return response;
}

export const fetchCourseContent = async (studentId: number, courseId: number) => {
  const response = await api.get(`/coursework/getCourseContent/${studentId}/${courseId}`);
  return response.data;
}

export const fetchStudentCourses = async (studentId: number) => {
  const response = await api.get(`/coursework/getStudentCourses/${studentId}`);
  return response.data;
}

export const closeOpenRewards = async (courseId: number, lessonId: number) => {
  const response = await api.get(`/coursework/closeOpenRewards`, { params: { courseId, lessonId } });
  return response.data;
}

export const checkForRewards = async (courseId: number) => {
  const response = await api.get(`/coursework/checkForRewards/${courseId}`);
  return response.data;
}

export const updateCoins = async (studentId: number, courseId: number, coins: number) => {
  const response = await api.put('/coursework/updateCoins', { studentId, courseId, coins });
  return response.data;
}

export const fetchGroupCoins = async (courseId: number, groupId: number) => {
  const response = await api.get(`/coursework/getGroupCoins/${courseId}/${groupId}`);
  return response.data
}

export const fetchStudentCoins = async (courseId: number, studentId: number) => {
  const response = await api.get(`/coursework/getStudentCoins/${courseId}/${studentId}`);
  return response.data
}

export const fetchGroupStats = async (groupId: number) => {
  const response = await api.get(`coursework/getGroupStats/${groupId}`);
  return response.data
}

export const fetchGroupData = async (groupId: number) => {
  const response = await api.get(`coursework/getGroupData/${groupId}`);
  return response.data
}

export const fetchRewards = async (studentId: number, courseId: number) => {
  const response = await api.get(`/coursework/getRewards/${studentId}/${courseId}`);
  return response.data;
}

export const awardBadge = async (badgeData: any) => {
  const response = await api.post('/coursework/awardBadge', badgeData);
  return response.data;
}

export const fetchBadgeTitle = async (lessonId: number): Promise<{ badgeTitle: string; awardTrophy: boolean }> => {
  const response = await api.get(`/coursework/getBadgeTitle/${lessonId}`);
  return response.data;
}

export const fetchTags = async (courseId: number, searchTerm: string) => {
  const response = await api.get(`/coursework/searchTags`, { params: { courseId, searchTerm } });
  return response.data;
}

export const createTask = async (data: any) => {
  const response = await api.post('/lessonwork/createTask', data);
  return response.data;
}

export const editTask = async (taskId: number, data: any) => {
  const response = await api.put(`/lessonwork/editTask/${taskId}`, data)
  return response.data;
}

export const submitHomework = async (data: any) => {
  const response = await api.post('/lessonwork/submitHomework', data);
  reachGoal('HOMEWORK_SUBMITTED');
  return response.data;
}

// export const fetchStudentLessons = async (unitId: number) => {
//   const response = await api.get(`/lessonwork/getStudentLessons/${unitId}`);
//   return response.data;
// }

export const fetchStudentHomework = async (studentId: number, courseId: number) => {
  const response = await api.get(`/lessonwork/getStudentHomework/${studentId}/${courseId}`);
  return response.data;
}

export const assignHomework = async (data: any) => {
  const response = await api.post('/lessonwork/assignHomework', data);
  reachGoal('HOMEWORK_ASSIGNED');
  return response.data;
}

export const unassignHomework = async (data: any) => {
  const response = await api.delete('/lessonwork/unassignHomework', { data });
  return response.data;
}

export const fetchCurrentHomework = async (studentId: number, courseId: number) => {
  const response = await api.get(`/lessonwork/getCurrentHomework/${studentId}/${courseId}`);
  return response.data;
}

export const fetchTaskAssignments = async (lessonId: number, entityType: 'student' | 'group', entityId: number) => {
  const response = await api.get(`/lessonwork/getTaskAssignments/${lessonId}/${entityType}/${entityId}`);
  return response.data;
}

export const fetchHomeworkTask = async (homeworkId: number, submitNumber: number) => {
  const response = await api.get(`/lessonwork/getHomeworkTask/${homeworkId}/${submitNumber}`);
  return response.data;
}

export const submitTest = async (data: any) => {
  // Longer than the default 15s: timing out on a slow save makes the student
  // retry, and the server would record the retry as a second attempt.
  const response = await api.post('/lessonwork/submitTest', data, { timeout: 60000 });
  reachGoal('TEST_SUBMITTED');
  return response.data;
}

export const fetchStudentTests = async (studentId: number, courseId: number) => {
  const response = await api.get(`/lessonwork/getStudentTests/${studentId}/${courseId}`);
  return response.data;
}

export const fetchAssignedTests = async (studentId: number, courseId: number) => {
  const response = await api.get(`/lessonwork/getAssignedTests/${studentId}/${courseId}`);
  return response.data;
}

export const editTestSettings = async (data: any) => {
  const response = await api.put(`/lessonwork/editTestSettings`, data);
  return response.data;
}

export const isTaskCompleted = async (taskId: number) => {
  const response = await api.get(`/lessonwork/isTaskCompleted/${taskId}`);
  return response.data;
}

export const submitGrade = async (data: any) => {
  const response = await api.put(`/lessonwork/submitGrade`, data);
  return response.data;
}

export const fetchFeedback = async (studentId: number, taskId: number, submitNumber: number, feedbackType: string) => {
  const response = await api.get(`/lessonwork/getFeedback/${studentId}/${taskId}/${submitNumber}/${feedbackType}`);
  return response.data;
}

export const updateGrade = async (data: any) => {
  const response = await api.put(`/lessonwork/updateGrade`, data)
  return response.data;
}

export const register = async (inputs: any) => {
  const response = await api.post("/auth/register", inputs);
  return response.data;
}

export const registerInvitedStudent = async (inputs: any) => {
  const response = await api.post("/auth/registerInvitedStudent", inputs);
  return response.data
}

export const callLogin = async (inputs: any) => {
  const response = await api.post("/auth/login", inputs);
  return response.data;
}

export const callCheckEmail = async (email: string) => {
  const response = await api.post("/auth/checkEmail", { email });
  return response.data;
}

export const callCheckUsername = async (username: string) => {
  const response = await api.post("/auth/checkUsername", { username });
  return response.data;
}

export const callLogout = async () => {
  const response = await api.post("/auth/logout");
  return response.data;
}

// export const callRefreshToken = async() => {
//   const response = await api.post("/auth/refreshToken", {}, { withCredentials: true });
//   return response.data
// }

export const changePassword = async (currentPassword: string, password: string) => {
  const response = await api.put('/auth/changePassword', { currentPassword, password });
  return response.data
}

export const changeName = async (name: string, surname: string) => {
  const response = await api.put('/auth/changeName', { name, surname });
  return response.data
}

export const changeEmail = async (email: string) => {
  const response = await api.put('/auth/changeEmail', { email });
  return response.data
}

export const changeDob = async (dob: string) => {
  const response = await api.put('/auth/changeDob', { dob });
  return response.data
}

export const changePhoneNumber = async (phone: string) => {
  const response = await api.put('/auth/changePhoneNumber', { phone });
  return response.data
}

export const changeAvatar = async (filename: string) => {
  const response = await api.put('/auth/changeAvatar', { filename });
  return response.data
}

export const codeVerification = async (code: string) => {
  const response = await api.patch(`/auth/verify/${code}`, { code });
  return response.data
}

export const fetchUnverifiedStudents = async (urlCode: string, studentId: number) => {
  const response = await api.get(`/auth/getUnverifiedStudent/${urlCode}/${studentId}`, {
    timeout: 10000,
  });
  return response.data
}

export const forgotPassword = async (email: string, language: string) => {
  const response = await api.post('/auth/forgotPassword', { email, language });
  return response.data
}

export const inviteStudent = async (data: any) => {
  const response = await api.post('/auth/inviteStudent', data);
  reachGoal('STUDENT_INVITED');
  return response.data
}

export const resetPassword = async (token: string, password: string) => {
  const response = await api.post('/auth/resetPassword', { token, password });
  return response.data
}

export const changeUserLang = async (language: string) => {
  const response = await api.put('/auth/changeLanguage', { language })
  return response.data
}

export const fetchTeacherProfile = async (teacherId: number) => {
  const response = await api.get(`/auth/getTeacherProfile/${teacherId}`)
  return response.data
}

export const changeBanner = async (filename: string, bgPosX: number, bgPosY: number) => {
  const response = await api.put('/auth/changeBanner', { filename, bgPosX, bgPosY });
  return response.data
}

export const changeQuote = async (quote: string) => {
  const response = await api.put('/auth/changeQuote', { quote });
  return response.data
}

export const changeAbout = async (about: string) => {
  const response = await api.put('/auth/changeAbout', { about });
  return response.data
}

export const deleteAccount = async (email: string, name: string, language: string) => {
  const response = await api.post(`/auth/deleteAccount`, { email, name, language });
  return response.data
}

export const getReferralCode = async (): Promise<{ referralCode: string }> => {
  const response = await api.get('/auth/getReferralCode');
  return response.data;
}

export const confirmDeleteAccount = async (token: string) => {
  const response = await api.get("/auth/confirmDeleteAccount", { params: { token } });
  return response.data
}

export const createNewEvent = async (data: any) => {
  const response = await api.post('/auth/createNewEvent', data);
  return response.data
}

export const fetchEvents = async () => {
  const response = await api.get('/auth/getEvents')
  return response.data
}

export const deleteEvent = async (eventId: number) => {
  const response = await api.delete(`/auth/deleteEvent/${eventId}`);
  return response.data
}

export const editEvent = async (eventId: number, data: any) => {
  const response = await api.put(`/auth/editEvent/${eventId}`, data)
  return response.data
}

export const deleteAllEvents = async (rcode: number) => {
  const response = await api.delete(`/auth/deleteAllEvents/${rcode}`);
  return response.data
}

export const createGroup = async (data: any) => {
  const response = await api.post('/auth/createGroup', data)
  return response.data
}

export const fetchStudents = async () => {
  const response = await api.get(`/auth/getStudents`);
  return response.data
}

export const fetchGroups = async () => {
  const response = await api.get(`/auth/getGroups`);
  return response.data
}


export const fetchStudentProfile = async (studentId: number) => {
  const response = await api.get(`/auth/getStudentProfile/${studentId}`);
  return response.data
}

export const deleteStudent = async (data: any) => {
  const response = await api.delete(`/auth/deleteStudent`, { data });
  return response.data
}

export const fetchAllRewards = async () => {
  const response = await api.get('/auth/getAllRewards');
  return response.data
}

export const fetchStudentsAndGroups = async () => {
  const response = await api.get('/auth/getStudentsAndGroups');
  return response.data
}

export const fetchStudentRewards = async (studentId: number) => {
  const response = await api.get(`/auth/getStudentRewards/${studentId}`);
  return response.data
}

// export const uploadImage = async (filename: string, formData: FormData) => {
//   const response = await api.post(`/filework/image/${filename}`, formData)
//   return response.data.filename;
// };

export const uploadImage = async (filename: string, formData: FormData) => {
  const response = await fetchWithAuthRetry(`${base}/filework/image/${filename}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

 if (!response.ok) {
  const text = await response.text();

  let errorCode = "ERROR_UPLOADING_FILE"; 

  try {
    const parsed = JSON.parse(text);
    if (parsed?.message) {
      errorCode = parsed.message;
    }
  } catch {
    // response was not JSON → keep fallback or raw text
  }

  console.log(`Upload image failed: ${response.status} - ${errorCode}`);
  throw new Error(errorCode);
}

  const data = await response.json();
  return data.filename;
};

// export const uploadCourseImage = async (filename: string, formData: any, isLessonImage: boolean = false) => {
//   const response = await api.post(`/filework/courseImage/${filename}?isLessonImage=${isLessonImage}`, formData)
//   return response.data
// }

export const uploadCourseImage = async (filename: string, formData: any, isLessonImage: boolean = false) => {
  const response = await fetchWithAuthRetry(`${base}/filework/courseImage/${filename}?isLessonImage=${isLessonImage}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

  if (!response.ok) {
    const text = await response.text();

    let errorCode = "ERROR_UPLOADING_FILE"; 

    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) {
        errorCode = parsed.message;
      }
    } catch {
      // response was not JSON → keep fallback or raw text
    }

    console.log(`Upload course image failed: ${response.status} - ${errorCode}`);
    throw new Error(errorCode);
  }

  const data = await response.json();
  return data;
};

export const deleteImage = async (imgPath: string) => {
  const response = await api.delete(`/filework/deleteImage`, {
    data: { imgPath }
  });
  return response.data;
};

// export const uploadAudio = async (filename: string, formData: any) => {
//   const response = await api.post(`/filework/audio/${filename}`, formData);
//   return response.data
// }

export const uploadAudio = async (filename: string, formData: any) => {
  const response = await fetchWithAuthRetry(`${base}/filework/audio/${filename}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

  if (!response.ok) {
    const text = await response.text();

    let errorCode = "ERROR_UPLOADING_FILE"; 

    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) {
        errorCode = parsed.message;
      }
    } catch {
      // response was not JSON → keep fallback or raw text
    }

    console.log(`Upload audio failed: ${response.status} - ${errorCode}`);
    throw new Error(errorCode);
  }

  return
};

export const deleteAudio = async (filename: string) => {
  const response = await api.delete(`/filework/deleteAudio`, {
    data: { filename }
  });
  return response.data;
};

// export const uploadPdfFile = async (filename: string, formData: any) => {
//   const response = await api.post(`filework/pdf/${filename}`, formData);
//   return response.data
// }

export const uploadPdfFile = async (filename: string, formData: any) => {
  const response = await fetchWithAuthRetry(`${base}/filework/pdf/${filename}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

  if (!response.ok) {
    const text = await response.text();

    let errorCode = "ERROR_UPLOADING_FILE"; 

    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) {
        errorCode = parsed.message;
      }
    } catch {
      // response was not JSON → keep fallback or raw text
    }

    console.log(`Upload pdf failed: ${response.status} - ${errorCode}`);
    throw new Error(errorCode);
  }

  return
};

export const deletePdf = async (filename: string, lessonId: number) => {
  const response = await api.delete(`/filework/deletePdf`, {
    data: { filename, lessonId }
  });
  return response.data;
};

// export const updateProfile = async (filename: string, formData: FormData) => {
//   const response = await api.post(`/filework/profile/${filename}`, formData);
//   return response.data
// }

export const updateProfile = async (filename: string, formData: FormData) => {
  const response = await fetchWithAuthRetry(`${base}/filework/profile/${filename}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

  if (!response.ok) {
    const text = await response.text();

    let errorCode = "ERROR_UPLOADING_FILE"; 

    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) {
        errorCode = parsed.message;
      }
    } catch {
      // response was not JSON → keep fallback or raw text
    }

    console.log(`Update profile failed: ${response.status} - ${errorCode}`);
    throw new Error(errorCode);
  }

  const data = await response.json();
  return data;
};

export const getFiles = async (fileType: string) => {
  const response = await api.get(`/filework/getFiles`, {
    params: { fileType }
  });
  return response.data;
};

export const cancelInvite = async (studentCourseId: number, studentId: number, teacherId: number) => {
  const response = await api.delete(`/auth/cancelInvite/${studentCourseId}/${studentId}/${teacherId}`);
  return response.data
}

export const wallPost = async (teacherId: number, post: string) => {
  const response = await api.post('/auth/wallPost', { teacherId, post });
  return response.data
}

export const getWallPosts = async (teacherId: number) => {
  const response = await api.get(`/auth/getWallPosts/${teacherId}`);
  return response.data
}

export const getLeaderboard = async (teacherId: number) => {
  const response = await api.get(`/auth/getLeaderboard/${teacherId}`);
  return response.data
}

// export const uploadProfileImage = async (formData: any, imageType: string) => {
//   const response = await api.post(`/filework/uploadProfileImage/${imageType}`, formData);
//   return response.data;
// };

export const uploadProfileImage = async (formData: any, imageType: string, originalFile: string ) => {

  if (originalFile != null) { 
    formData.append("originalFile", originalFile);
  }
  const response = await fetchWithAuthRetry(`${base}/filework/uploadProfileImage/${imageType}`, {
    method: "POST",
    body: formData,
    credentials: "include", // <-- important if your access token is in a cookie
  });

  if (!response.ok) {
    const text = await response.text();

    let errorCode = "ERROR_UPLOADING_FILE"; 

    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) {
        errorCode = parsed.message;
      }
    } catch {
      // response was not JSON → keep fallback or raw text
    }

    console.log(`Upload profile image failed: ${response.status} - ${errorCode}`);
    throw new Error(errorCode);
  }

  return
};

export const contactTeacher = async (teacherId: number, studentEmail: string, captchaToken: string) => {
  const response = await api.post(`/auth/contactTeacher`, { teacherId, studentEmail, captchaToken });
  return response.data;
}

export const deleteTask = async(taskId: number) => {
  const response = await api.delete(`/lessonwork/deleteTask/${taskId}`);
  return response.data;
}

export const saveTaskOrder= async (reorderTasks: any, lessonId: number) => {
  const response = await api.put(`/lessonwork/saveTaskOrder`, {reorderTasks, lessonId});
  return response.data;
}

export const savePdfLinks = async (lessonId: number, filename: string, pdfLinks: any) => {
  const response = await api.post(`/lessonwork/savePdfLinks`, {lessonId, filename, pdfLinks});
  return response.data;
}

export const aiRequest = async (prompt: string) => {
  const response = await api.post(`/auth/aiRequest`, {prompt}, { timeout: 60000 });
  reachGoal('AI_USED');
  return response.data;
}

export const aiTaskRequest = async (prompt: string, taskType: string) => {
  const response = await api.post(`/auth/aiTaskRequest`, { prompt, taskType }, { timeout: 60000 });
  reachGoal('AI_USED');
  return response.data;
}

export const aiLessonTaskRequest = async (image: File | null, taskType: string, taskNumber: number, totalTasks: number, prompt?: string, languagePoint?: string, level?: string, targetLanguage?: string, topic?: string, stage?: string, readingText?: string) => {
  const formData = new FormData();
  if (image) formData.append('image', image);
  formData.append('taskType', taskType);
  formData.append('taskNumber', String(taskNumber));
  formData.append('totalTasks', String(totalTasks));
  if (prompt?.trim()) formData.append('prompt', prompt.trim());
  if (languagePoint?.trim()) formData.append('languagePoint', languagePoint.trim());
  if (level?.trim()) formData.append('level', level.trim());
  if (targetLanguage?.trim()) formData.append('targetLanguage', targetLanguage.trim());
  if (topic?.trim()) formData.append('topic', topic.trim());
  if (stage?.trim()) formData.append('stage', stage.trim());
  if (readingText?.trim()) formData.append('readingText', readingText.trim());
  const response = await fetchWithAuthRetry(`${base}/auth/aiLessonTaskRequest`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err: any = new Error(data?.message || 'Request failed');
    err.response = { data };
    throw err;
  }
  return response.json();
};

export const aiLessonPlanRequest = async (languagePoint: string, image?: File | null, taskCount?: number, level?: string, lessonType?: string, targetLanguage?: string, topic?: string) => {
  const formData = new FormData();
  if (languagePoint.trim()) formData.append('languagePoint', languagePoint.trim());
  if (image) formData.append('image', image);
  if (taskCount) formData.append('taskCount', String(taskCount));
  if (level?.trim()) formData.append('level', level.trim());
  if (lessonType?.trim()) formData.append('lessonType', lessonType.trim());
  if (targetLanguage?.trim()) formData.append('targetLanguage', targetLanguage.trim());
  if (topic?.trim()) formData.append('topic', topic.trim());
  const response = await fetchWithAuthRetry(`${base}/auth/aiLessonPlanRequest`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err: any = new Error(data?.message || 'Request failed');
    err.response = { data };
    throw err;
  }
  reachGoal('AI_USED');
  return response.json();
};

export const aiWorksheetPlanRequest = async (image: File, taskTypes: string[], level?: string, targetLanguage?: string) => {
  const formData = new FormData();
  formData.append('image', image);
  formData.append('taskTypes', JSON.stringify(taskTypes));
  if (level?.trim()) formData.append('level', level.trim());
  if (targetLanguage?.trim()) formData.append('targetLanguage', targetLanguage.trim());
  const response = await fetchWithAuthRetry(`${base}/auth/aiWorksheetPlanRequest`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err: any = new Error(data?.message || 'Request failed');
    err.response = { data };
    throw err;
  }
  reachGoal('AI_USED');
  return response.json();
};

export const getUnitSkillCategories = async (unitId: number): Promise<string[]> => {
  const response = await api.get(`/auth/unitSkillCategories`, { params: { unitId } });
  return response.data?.categories ?? [];
};

export const aiTestPlanRequest = async (unitId: number, tasks: { type: string; skillCategory: string }[]) => {
  const response = await fetchWithAuthRetry(`${base}/auth/aiTestPlanRequest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ unitId, tasks: JSON.stringify(tasks) }),
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err: any = new Error(data?.message || 'Request failed');
    err.response = { data };
    throw err;
  }
  reachGoal('AI_USED');
  return response.json();
};

export const aiTaskImageRequest = async (image: File, taskType: string, prompt?: string) => {
  const buffer = await image.arrayBuffer();
  const mimeType = image.type || 'image/jpeg';
  const blob = new Blob([buffer], { type: mimeType });
  const formData = new FormData();
  formData.append('image', blob, image.name || 'image.jpg');
  formData.append('taskType', taskType);
  if (prompt?.trim()) formData.append('prompt', prompt.trim());
  const response = await fetchWithAuthRetry(`${base}/auth/aiTaskImageRequest`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err: any = new Error(data?.message || 'Request failed');
    err.response = { data };
    throw err;
  }
  return response.json();
}

export const getProfileImage = async (filename: string) => {
  // const response = await api.get(`/filework/getProfileImage/${filename}`, {
  //   responseType: "blob", 
  // });
  const url = `/teachers_material/profile/${filename}`;
  // const response = await fetch(url);
  // return URL.createObjectURL(response.data);
  return url
};

export const getLessonPdf = async (filename: string): Promise<Blob> => {
  // const response = await api.get(`/filework/getPdf/${filename}`, {
  //   responseType: 'blob', // important for binary data
  // });
  const url = `/teachers_material/pdf/${filename}`
  const response = await fetch(url);
  return await response.blob();
};

// export const getImage = async (filename: string): Promise<string> => {
//   const response = await api.get(`/filework/getImage/${filename}`, {
//     responseType: 'blob', // important for binary files
//   });
//   return URL.createObjectURL(response.data); // Blob → URL
// };

// export const getAudio = async (filename: string): Promise<string> => {
//   const response = await api.get(`/filework/getAudio/${filename}`, {
//     responseType: 'blob',
//   });
//   return URL.createObjectURL(response.data);
// };

// export const getCourseImage = async (filename: string): Promise<string> => {
//   const response = await api.get(`/filework/getCourseImage/${filename}`, {
//     responseType: 'blob',
//   });
//   return URL.createObjectURL(response.data)
// };



export const deleteGroup = async (groupId: number) => {
  const response = await api.delete(`auth/deleteGroup/${groupId}`);
  return response.data
};

export const fetchStudentInfo = async (studentId: number, courseId: number) => {
  const response = await api.get(`/coursework/getStudentInfo/${studentId}/${courseId}`); 
  return response.data
}

export const updateCourseOrder = async (type: 'units'|'lessons', items: {id: number, order: number}[]) => {
  const response = await api.put('/coursework/updateCourseOrder', {
    type,
    items,
  });

  return response.data;
};

export const fetchTestsStats = async (studentId: number, courseId: number) => {
  const response = await api.get(`coursework/getTestsStats/${studentId}/${courseId}`); 
   return response.data
}

export const fetchHomeworkStats = async (studentId: number, courseId: number) => {
  const response = await api.get(`coursework/getHomeworkStats/${studentId}/${courseId}`); 
   return response.data
}

export const fetchSkills = async () => {
  const response = await api.get(`lessonwork/getSkills`); 
  return response.data
}

export const saveSkills = async (skill: string) => {
  const response = await api.post(`lessonwork/saveSkills`, {skill}); 
  return response.data
}

export const deleteSkill = async (skillId: number) => {
  const response = await api.delete(`/lessonwork/deleteSkill/${skillId}`);
  return response.data;
};

export const addKnowledge = async(courseId: number, classId: number, knowledgeTitle: string) => {
  const response = await api.post(`/coursework/addKnowledge`, {courseId, classId, knowledgeTitle});
  return response.data;
}

export const fetchKnowledges = async(courseId: number, classId: number) => {
  const response = await api.get(`coursework/getKnowledges/${courseId}/${classId}`)
  return response.data
}

export const addSelfQuizItem = async(courseId: number, studentId: number, item: string, answer: string) => {
  const response = await api.post(`/coursework/addSelfQuizItem`, {courseId, studentId, item, answer});
  return response.data;
}

export const fetchSelfQuizItems = async(courseId: number, studentId: number) => {
  const response = await api.get(`coursework/getSelfQuizItems/${courseId}/${studentId}`)
  return response.data
}

export const deleteSelfQuizItem = async(selfQuizId: number) => {
  const response = await api.delete(`/coursework/deleteSelfQuizItem/${selfQuizId}`);
  return response.data;
}

export const fetchKnowledgeContent = async(knowledgesId: number) => {
  const response = await api.get(`coursework/getKnowledgeContent/${knowledgesId}`)
  return response.data
}

export const updateKnowledges = async (updated: { knowledgesId: number; knowledgeTitle: string }[], deleted: number[]) => {
  const response = await api.put(`/coursework/updateKnowledges`, { updated, deleted });
  return response.data;
};

export const deleteKnowledgeContentItem = async(knowledgeBankId: number) => {
  const response = await api.delete(`/coursework/deleteKnowledgeContentItem/${knowledgeBankId}`);
  return response.data;
}


export const getAdminOverview = async () => {
  const response = await api.get('/admin/overview');
  return response.data;
};

export const getAdminUserGrowth = async () => {
  const response = await api.get('/admin/user-growth');
  return response.data;
};

export const getAdminLogs = async () => {
  const response = await api.get('/admin/logs');
  return response.data;
};

export const getAdminAiStats = async () => {
  const response = await api.get('/admin/ai-stats');
  return response.data;
};

export default api;